// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Tithe — every screen's markup. Kept away from main.ts so the screen machine stays about state and
// this stays about words, and so the copy can be read in one place when it needs to be honest
// (what the multiplayer relay sees, what a curated rule does, what the bot is actually doing).

import { escapeHtml } from './dom';
import { MODE_IDS, modeOf, type Mode } from './modes';
import { TIERS, tierOf } from './bot';
import { SEAT_NAME } from './palette';
import { tollHtml } from './render';
import { TOLLS } from './tolls';
import type { WinReason } from './rules';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

const LOGO = `<svg viewBox="0 0 120 64" aria-hidden="true">
  <path d="M86 34a22 22 0 1 1-8-18" fill="none" stroke="var(--gold)" stroke-width="8" stroke-linecap="round"/>
  <path d="M74 4 L92 18 L66 24 Z" fill="var(--gold)"/>
  <circle cx="64" cy="34" r="14" fill="var(--p1)"/>
  <circle cx="64" cy="34" r="14" fill="none" stroke="var(--gold)" stroke-width="4"/>
  <path d="M18 20 L32 34 L18 48 L4 34 Z" fill="var(--p2)"/>
</svg>`;

export interface MenuOpts {
  modeId: string;
  tierId: string;
  record: [number, number];
  muted: boolean;
}

export function menuHtml(o: MenuOpts): string {
  const mode = modeOf(o.modeId);
  const [w, l] = o.record;
  return `
    <section class="screen menu">
      <div class="brand">
        <div class="logo">${LOGO}</div>
        <h1>Tithe</h1>
        <p class="tagline">Spend a toll to move — then hand that toll to your opponent.</p>
      </div>

      <div>
        <p class="field-label">Game</p>
        <div class="chips">${MODE_IDS.map(
          (id) =>
            `<button class="chip${id === o.modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === o.modeId}">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}</div>
        <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
        <p class="mode-best">${w + l > 0 ? `Your record in ${escapeHtml(mode.name)}: ${w}W · ${l}L` : 'No games in this one yet.'}</p>
      </div>

      <div>
        <p class="field-label">Opponent</p>
        <div class="chips">${TIERS.map(
          (t) => `<button class="chip${t.id === o.tierId ? ' on' : ''}" data-tier="${t.id}" aria-pressed="${t.id === o.tierId}">${escapeHtml(t.name)}</button>`,
        ).join('')}</div>
        <p class="mode-blurb">${escapeHtml(tierOf(o.tierId).blurb)}</p>
      </div>

      <div class="menu-actions">
        <button class="btn primary" id="playSolo">Play</button>
        <button class="btn" id="playFriends">Play with a friend</button>
      </div>
      <div class="menu-links">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound off' : 'Sound on'}</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `<button class="sheet-close x" aria-label="Close">✕</button>
    <h2>How to play</h2>
    <ol class="help-list">
      <li>You hold <strong>two tolls</strong>. A toll is a small pattern of jumps. Tap one of your pieces, then tap a highlighted square to move it there.</li>
      <li>Moving <strong>spends</strong> that toll: it goes onto the table, and you pick up the toll that was already sitting there. So the tool you just used is the tool your opponent gets next.</li>
      <li>Landing on an enemy piece takes it. <strong>Win by taking their Keeper</strong> — the piece wearing a gold ring — <strong>or by walking your own Keeper onto their coffer</strong>, the marked square in the middle of their back row.</li>
      <li>Before the first move, one player <strong>cuts</strong> the dealt tolls into two pairs and the other <strong>picks</strong> one. Cut them evenly: whoever picks gets the first move too.</li>
    </ol>
    <h3>The small print</h3>
    <ul class="help-list small">
      <li>If nothing you own can move, you still pay a toll — pick which one you can bear to give away.</li>
      <li><strong>The levy.</strong> Go too long without anything being taken and the tide collects: whoever has <em>more</em> pieces standing loses their rearmost Bearer, and if you are level you both do. Stalling on a lead is the one thing it punishes.</li>
      <li>On a phone, tap a toll card first if you want to see only the moves that toll offers.</li>
    </ul>
    <h3>The tolls</h3>
    <div class="help-figure">${[0, 5, 9].map((i) => tollHtml(TOLLS[i])).join('')}</div>
    <p class="help-note">The dot in the middle is your piece; the pale squares are where it may land. The far side reads the same card upside down, which is why their cards look flipped.</p>`;
}

export function aboutHtml(): string {
  return `<button class="sheet-close x" aria-label="Close">✕</button>
    <h2>About Tithe</h2>
    <p>A two-player abstract about paying for your own moves. It takes the classic "your hand of moves rotates to your opponent" idea and builds an original game around it — original board, original deck of sixteen tolls, an opening where one player cuts and the other chooses, and three ways for a game to end.</p>
    <h3>The opponent</h3>
    <p>Solo games are played against a real engine: alpha–beta search with iterative deepening over the same rules you are playing. Three strengths — Novice never blunders a win but sets no traps; Reckoner searches several turns deep and counts what you can do with the toll it just handed you.</p>
    <h3>Playing with a friend</h3>
    <p>Games with a friend are <strong>peer to peer</strong>. There is no game server and no account: the two browsers talk directly over WebRTC, and a public signalling relay is used only to introduce them. Nothing about your game is stored anywhere.</p>
    <h3>Privacy</h3>
    <p>No cookies, no fingerprinting, no third-party fonts, and nothing to sign up for. Page views are counted anonymously with Cloudflare Web Analytics, which sets no cookies.</p>
    <p class="about-attrib">${ATTRIB}</p>`;
}

export interface ResultRow {
  seat: number;
  name: string;
  isSelf: boolean;
  isBot: boolean;
  won: boolean;
  turns: number;
  takes: number;
  lost: number;
  closest: number;
  threats: number;
  mobility: number;
  slack: number;
  left: boolean;
}

const HEADLINE: Record<WinReason, (n: string) => string> = {
  '': () => 'Round over',
  seize: (n: string) => `${n} takes the Keeper`,
  collect: (n: string) => `${n} reaches the coffer`,
  drained: (n: string) => `${n} outlasts the levy`,
  walkover: (n: string) => `${n} wins — the other side left`,
  nocontest: () => 'No contest',
};

const SUB: Record<WinReason, string> = {
  '': '',
  seize: 'The ringed piece came off the board, and that is the whole game.',
  collect: 'A Keeper walked all the way into the enemy coffer.',
  drained: 'The levy took everything but the Keepers, and the one that had walked further won.',
  walkover: 'Your opponent closed the room mid-round.',
  nocontest: 'Nobody had played a move, so nothing was awarded.',
};

export interface ResultsOpts {
  rows: ResultRow[];
  mode: Mode;
  reason: WinReason;
  winner: number;
  tally: [number, number];
  round: number;
  fault: null | 'desync' | 'build';
  /** The post-game read: turns on which a player had a win on the board and played something else. */
  missed: [number, number];
  missedNote: string;
}

export function resultsHtml(o: ResultsOpts): string {
  const winnerName = o.winner >= 0 ? o.rows[o.winner]?.name ?? SEAT_NAME[o.winner] : '';
  const head = o.fault
    ? o.fault === 'build'
      ? 'Different builds'
      : 'The boards stopped agreeing'
    : HEADLINE[o.reason]?.(winnerName) ?? 'Round over';
  const sub = o.fault
    ? o.fault === 'build'
      ? 'One of you is on a cached older version. Reload and the round will run.'
      : 'The round was stopped rather than played out on two different boards.'
    : SUB[o.reason] ?? '';

  const rows = o.rows
    .map((r) => {
      const missed = o.missed[r.seat];
      return `<div class="rrow${r.won ? ' won' : ''}">
        <div class="rname"><span class="rtok p${r.seat + 1}"></span>${escapeHtml(r.name)}${r.isSelf ? ' (you)' : ''}${r.isBot ? ' · bot' : ''}${r.left ? ' · left' : ''}</div>
        <p class="rverdict">${r.won ? 'Winner' : o.winner < 0 ? '—' : 'Runner-up'} · ${escapeHtml(SEAT_NAME[r.seat])}</p>
        <dl class="rstats">
          <div><dt>Turns</dt><dd>${r.turns}</dd></div>
          <div><dt>Taken</dt><dd>${r.takes}</dd></div>
          <div><dt>Lost</dt><dd>${r.lost}</dd></div>
          <div><dt>Keeper got to</dt><dd>${r.closest === 0 ? 'coffer' : `${r.closest} away`}</dd></div>
          <div><dt>Threats made</dt><dd>${r.threats}</dd></div>
          <div><dt>Wins missed</dt><dd>${missed}</dd></div>
        </dl>
      </div>`;
    })
    .join('');

  return `
    <div class="results-head">
      <h2>${escapeHtml(head)}</h2>
      <p class="results-sub">${escapeHtml(sub)}</p>
      <p class="results-tally">${escapeHtml(o.mode.name)} · round ${o.round} · match ${o.tally[0]}–${o.tally[1]}</p>
    </div>
    <canvas class="finalboard" id="finalBoard" width="640" height="640" aria-label="The final position"></canvas>
    <div class="verdict" id="verdict"><p class="verdict-line">${escapeHtml(o.missedNote)}</p></div>
    <div class="rrows">${rows}</div>`;
}
